import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-purchase-order-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1400px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/financials/purchase-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Purchase Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">PO Reports</h1>
            <p class="text-slate-500 text-xs mt-1">Supplier-wise, item-wise, date-wise, and creator-wise purchasing views.</p>
          </div>
        </header>

        <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
            @for (tab of reportTabs; track tab.value) {
              <button type="button" (click)="setReport(tab.value)" class="h-9 px-3 rounded-lg text-xs font-semibold border"
                [class.bg-[#EAF5FA]]="filters.group_by === tab.value"
                [class.text-[#1A6FA6]]="filters.group_by === tab.value"
                [class.border-[#D5EBF6]]="filters.group_by === tab.value"
                [class.bg-white]="filters.group_by !== tab.value"
                [class.text-slate-500]="filters.group_by !== tab.value"
                [class.border-slate-200]="filters.group_by !== tab.value">
                {{ tab.label }}
              </button>
            }
            <div class="ml-auto flex items-center gap-2">
              <input [(ngModel)]="filters.date_from" (change)="loadReport()" type="date" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
              <input [(ngModel)]="filters.date_to" (change)="loadReport()" type="date" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </div>
          </div>
          <div class="overflow-x-auto min-h-[460px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Group</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">POs</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Qty</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Subtotal</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">GST</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">Total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (row of rows(); track row.label) {
                  <tr>
                    <td class="px-4 py-3 font-semibold text-slate-900">{{ row.label }}</td>
                    <td class="px-4 py-3 text-right text-slate-500">{{ row.po_count || 0 }}</td>
                    <td class="px-4 py-3 text-right text-slate-500">{{ row.quantity || '-' }}</td>
                    <td class="px-4 py-3 text-right text-slate-700">{{ row.subtotal | currency:'NZD':'symbol':'1.2-2' }}</td>
                    <td class="px-4 py-3 text-right text-slate-700">{{ row.tax | currency:'NZD':'symbol':'1.2-2' }}</td>
                    <td class="px-4 py-3 text-right font-bold text-slate-900">{{ row.total | currency:'NZD':'symbol':'1.2-2' }}</td>
                  </tr>
                }
                @if (!rows().length) {
                  <tr><td colspan="6" class="py-16 text-center text-slate-400"><i class="ti ti-chart-bar text-4xl mb-3 opacity-25"></i><p class="font-semibold text-sm">No report rows found.</p></td></tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  `
})
export class AdminPurchaseOrderReportsComponent implements OnInit {
  private api = inject(ApiService);
  rows = signal<any[]>([]);
  filters: any = { group_by: 'supplier', date_from: '', date_to: '' };
  reportTabs = [
    { label: 'Supplier wise', value: 'supplier' },
    { label: 'Item wise', value: 'item' },
    { label: 'Date wise', value: 'date' },
    { label: 'Created by', value: 'created_by' }
  ];

  ngOnInit() {
    this.loadReport();
  }

  setReport(groupBy: string) {
    this.filters.group_by = groupBy;
    this.loadReport();
  }

  loadReport() {
    this.api.getPurchaseOrderReports(this.filters).subscribe({
      next: (res: any) => this.rows.set(res.data?.rows || [])
    });
  }
}
